import { morph, MORPH_OPTIONS } from "@theme/morph";
import { normalizeSectionId } from "@theme/section-renderer";
import { startViewTransition } from "@theme/utilities";

const SELECTORS = {
  mainContent: "#MainContent",
  switcher: "[data-collection-switcher]",
  link: "[data-collection-switch-link]",
};

const SECTION_ID_PREFIX = "shopify-section-";

const CACHE_MAX = 8;
const CACHE_TTL = 300000;
const PREFETCH_LIMIT = 3;

const sectionCache = new Map();
const instances = new WeakMap();
const instanceList = new Set();

const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));

function canPrefetch() {
  const conn = navigator.connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  if (conn.effectiveType === "2g") return false;
  return true;
}

function createCacheKey(url) {
  const u = new URL(url.toString());
  u.hash = "";

  const allowed = ["sort_by", "page"];

  for (const key of [...u.searchParams.keys()]) {
    if (!allowed.includes(key) && !key.startsWith("filter.")) {
      u.searchParams.delete(key);
    }
  }

  u.searchParams.sort();
  return u.toString();
}

function buildSectionURL(url, ids) {
  const u = new URL(url);
  u.searchParams.set("sections", ids.join(","));
  return u.toString();
}

function evictCache() {
  while (sectionCache.size > CACHE_MAX) {
    const key = sectionCache.keys().next().value;
    sectionCache.delete(key);
  }
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function getCollectionHandle(url) {
  const segments = url.pathname.split("/").filter(Boolean);
  const index = segments.indexOf("collections");
  if (index === -1) return "";
  return segments[index + 1] || "";
}

class CollectionSwitcher {
  constructor(root) {
    this.root = root;
    this.requestId = 0;

    this.prefetchQueue = new Map();
    this.activePrefetches = 0;

    this.abortController = null;
    this.prefetchAbortController = new AbortController();

    this.observer = null;
    this.sectionIdsCache = null;
  }

  get mainContent() {
    return document.querySelector(SELECTORS.mainContent);
  }

  init() {
    if (!(this.mainContent instanceof HTMLElement)) return;
    if (window.Shopify?.designMode) return;

    this.root.addEventListener("click", this.onClick);
    window.addEventListener("popstate", this.onPopState);

    this.setupObserver();
    this.observeLinks();

    this.syncCurrentState(new URL(location.href));
    this.scheduleInitialPrefetch();
  }

  destroy() {
    this.root.removeEventListener("click", this.onClick);
    window.removeEventListener("popstate", this.onPopState);

    this.abortController?.abort();
    this.prefetchAbortController?.abort();

    if (this.observer) this.observer.disconnect();
  }

  invalidateSectionCache() {
    this.sectionIdsCache = null;
  }

  onClick = (event) => {
    const link = event.target.closest(SELECTORS.link);
    if (!(link instanceof HTMLAnchorElement)) return;

    const url = this.getURL(link);
    if (!url) return;

    event.preventDefault();

    this.syncCurrentState(url);
    this.navigate(url, true);
  };

  onPopState = () => {
    const url = new URL(location.href);
    this.syncCurrentState(url);
    this.navigate(url, false);
  };

  async navigate(url, push) {
    const requestId = ++this.requestId;

    this.abortController?.abort();
    this.prefetchAbortController?.abort();
    this.prefetchAbortController = new AbortController();

    this.abortController = new AbortController();
    this.sectionIdsCache = null;

    try {
      const sections = await this.getSections(url, this.abortController.signal);

      if (requestId !== this.requestId) return;

      await startViewTransition(() => {
        this.applyMorph(sections);
      });
      /* custom-code start */
      document.dispatchEvent(
        new CustomEvent("results-list:updated", {
          detail: { source: "collection-switcher" },
        }),
      );
      /* custom-code end */
      if (push) history.pushState({}, "", url.toString());

      if (push) window.scrollTo({ top: 0 });

      this.observeLinks();
    } catch (err) {
      if (err.name === "AbortError") return;

      if (window.Shopify?.designMode) console.error(err);

      location.href = url.toString();
    }
  }

  applyMorph(sections) {
    for (const id in sections) {
      const current = document.getElementById(`${SECTION_ID_PREFIX}${id}`);
      if (!current) continue;

      const template = document.createElement("template");
      template.innerHTML = sections[id];

      const next = template.content.firstElementChild;
      if (!next) continue;

      morph(current, next, MORPH_OPTIONS);
    }
  }

  getSectionIds() {
    if (this.sectionIdsCache) return this.sectionIdsCache;

    const root = this.mainContent;
    if (!root) return [];

    const ids = [];

    for (const section of root.children) {
      if (!(section instanceof HTMLElement)) continue;

      const id = normalizeSectionId(section.id);
      if (!id) continue;

      ids.push(id);
    }

    this.sectionIdsCache = ids;
    return ids;
  }

  async getSections(url, signal) {
    const key = createCacheKey(url);

    const cached = sectionCache.get(key);

    if (cached && Date.now() - cached.time < CACHE_TTL) {
      sectionCache.delete(key);
      sectionCache.set(key, cached);
      return cached.data;
    }

    const sections = await this.fetchSections(url, signal);

    sectionCache.set(key, { data: sections, time: Date.now() });

    evictCache();

    return sections;
  }

  async fetchSections(url, signal) {
    const ids = this.getSectionIds();
    const endpoint = buildSectionURL(url, ids);

    const options = {
      credentials: "same-origin",
      signal,
    };

    let response = await fetch(endpoint, options);

    if (!response.ok) {
      await Promise.race([
        delay(200),
        new Promise((_, reject) =>
          signal.addEventListener("abort", () =>
            reject(new DOMException("Aborted", "AbortError")),
          ),
        ),
      ]);

      response = await fetch(endpoint, options);

      if (!response.ok) throw new Error("Section fetch failed");
    }

    return response.json();
  }

  prefetch(url) {
    if (!canPrefetch()) return;
    if (this.activePrefetches >= PREFETCH_LIMIT) return;

    const key = createCacheKey(url);

    if (sectionCache.has(key)) return;
    if (this.prefetchQueue.has(key)) return;

    this.activePrefetches++;

    const promise = this.prefetchSections(
      url,
      this.prefetchAbortController.signal,
    )
      .then((data) => {
        sectionCache.set(key, { data, time: Date.now() });
        evictCache();
      })
      .catch(() => {})
      .finally(() => {
        this.activePrefetches--;
        this.prefetchQueue.delete(key);
      });

    this.prefetchQueue.set(key, promise);
  }

  async prefetchSections(url, signal) {
    const ids = this.getSectionIds();
    const endpoint = buildSectionURL(url, ids);

    const res = await fetch(endpoint, {
      credentials: "same-origin",
      signal,
    });

    if (!res.ok) throw new Error("Prefetch failed");

    return res.json();
  }

  setupObserver() {
    if (!("IntersectionObserver" in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const link = entry.target;
          if (!(link instanceof HTMLAnchorElement)) continue;

          const url = this.getURL(link);
          if (url) this.prefetch(url);

          this.observer.unobserve(link);
        }
      },
      {
        root: null,
        rootMargin: "120px",
      },
    );
  }

  observeLinks() {
    if (!this.observer) return;

    this.observer.disconnect();

    this.root.querySelectorAll(SELECTORS.link).forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        this.observer.observe(link);
      }
    });
  }

  scheduleInitialPrefetch() {
    if (!canPrefetch()) return;

    ric(() => {
      const links = [...this.root.querySelectorAll(SELECTORS.link)].slice(
        0,
        PREFETCH_LIMIT,
      );

      for (const link of links) {
        if (!(link instanceof HTMLAnchorElement)) continue;

        const url = this.getURL(link);
        if (url) this.prefetch(url);
      }
    });
  }

  getURL(link) {
    const href = link.getAttribute("href");
    if (!href) return null;

    const url = new URL(href, location.origin);
    if (url.origin !== location.origin) return null;

    return url;
  }

  syncCurrentState(url) {
    const handle = getCollectionHandle(url);

    this.root.querySelectorAll(SELECTORS.link).forEach((link) => {
      if (!(link instanceof HTMLElement)) return;

      const current = link.dataset.collectionHandle === handle;

      link.classList.toggle("collection-icon-scroller__link--current", current);

      if (current) link.setAttribute("aria-current", "page");
      else link.removeAttribute("aria-current");
    });
  }
}

function initCollectionSwitchers(root = document) {
  root.querySelectorAll(SELECTORS.switcher).forEach((node) => {
    if (!(node instanceof HTMLElement) || instances.has(node)) return;

    const instance = new CollectionSwitcher(node);
    instance.init();

    instances.set(node, instance);
    instanceList.add(instance);
  });
}

function teardownCollectionSwitchers(root = document) {
  root.querySelectorAll(SELECTORS.switcher).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;

    const instance = instances.get(node);
    if (!instance) return;

    instance.destroy();

    instances.delete(node);
    instanceList.delete(instance);
  });
}

document.addEventListener("shopify:section:load", (e) => {
  instanceList.forEach((instance) => instance.invalidateSectionCache());
  if (e.target instanceof HTMLElement) initCollectionSwitchers(e.target);
});

document.addEventListener("shopify:section:unload", () => {
  instanceList.forEach((instance) => instance.invalidateSectionCache());
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () =>
    initCollectionSwitchers(),
  );
} else {
  initCollectionSwitchers();
}
